<!--
    AssetFileUpload

    ** DESCRIPTION **
    General purpose component for uploading media to an asset object

    ** PROPS **
    - asset_type {str} => according to destination asset.type e.g. "vehicle". This is used to customise the UI and various settings
    - media_types {arr} => the types of media permitted to upload
-->

<template>
    <div class="mb-6">
        <FormFileUploadDropZone @files-dropped="addFiles">
            <svg class="mx-auto h-12 w-12 text-gray-400" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" stroke="currentColor" fill="none"  viewBox="0 0 48 48" aria-hidden="true">
			    <path d="M34.1,45.6c6.4,0,11.5-5.1,11.5-11.5c0-6.4-5.1-11.5-11.5-11.5c-6.4,0-11.5,5.1-11.5,11.5C22.6,40.4,27.7,45.6,34.1,45.6z"/>
			    <path d="M34.1,39.8V28.3"/>
			    <path d="M34.1,28.3l-4.3,4.3"/>
			    <path d="M34.1,28.3l4.3,4.3"/>
			    <path d="M8.2,13.9h20.1"/>
			    <path d="M8.2,22.6h11.5"/>
			    <path d="M8.2,31.2h7.2"/>
			    <path d="M15.4,39.8H5.3c-1.6,0-2.9-1.3-2.9-2.9V5.3c0-1.6,1.3-2.9,2.9-2.9h20.4c0.8,0,1.5,0.3,2,0.8l5.5,5.5c0.5,0.5,0.8,1.3,0.8,2v4.6"/>
            </svg>
            <div class="flex text-sm text-gray-600">
                <label :for="field_id" class="relative cursor-pointer rounded-md font-medium text-blue-600 focus-within:outline-none focus-within:ring-2 focus-within:ring-blue-500 focus-within:ring-offset-2 hover:text-blue-500">
                    <span>{{ upload_label }}</span>
                    <input :id="field_id" :name="field_id" type="file" class="sr-only" multiple v-on:change="onInputChange">
                </label>
                <p class="pl-1">or drag and drop</p>
            </div>
            <p class="text-xs text-gray-500">{{ allowed_types || 'documents up to 10MB' }}</p>
        </FormFileUploadDropZone>
    </div>

    <FormFileUploadQueue :files="files" class="mb-6" :removeFile="removeFile" :asset_id="asset_id" />

    <!--<Button v-on:click="uploadFiles(files)" :disabled="uploadable_files.length === 0">Upload</Button>-->
</template>

<script>
    import useFileList from '@lib/file-list'
    import * as AssetService from '@ser/asset'
    import { useAssetStore } from '@sto/asset.store'
    import { useSystemStore } from '@sto/system.store'
    import { getFilename } from '@lib/helpers'
    import { computed, watch } from 'vue';
    import { useRoute } from 'vue-router';
    export default {
        props: ['asset_id', 'asset_type', 'media_type', 'field_id', 'base_file_name', 'allowed_types', 'upload_label_text', 'hide_complete'],
        setup(props) {

            const ASSET_STORE = useAssetStore()
            const SYSTEM_STORE = useSystemStore()
            const ROUTE = useRoute()

            const { files, addFiles, removeFile, resetFileQueue } = useFileList()

            const uploadable_files = computed(() => {
                if (!Array.isArray(files.value)) return []
                return files.value.filter(f => f.status == 'ready') || []
            })

            const onInputChange = e => {
                addFiles(e.target.files)
                e.target.value = null
            }

            const timeout = ms => {
                return new Promise(resolve => setTimeout(resolve, ms));
            }

            const tempFileName = index => {
                return `asset-${props.asset_id}-${props.media_type}-${index}`
            }

            const getVisibilityByType = type => {
                switch(type) {
                    case 'image':
                    case 'air-report':
                    case 'spec-sheet':
                        return 'public'
                    default:
                        return 'private'
                }
            }

            const uploadFile = async (file, index) => {

                // make sure we're not trying to re-upload existing images 
                if (['complete', 'hidden'].includes(file.status)) return

                await timeout(1000 * index)

                const visibility = getVisibilityByType(props.media_type)

                file.status = 'loading'
                const payload = {
                    file: file.file,
                    file_name: file?.file?.name ? getFilename(file.file) : tempFileName(index),
                    type: props.media_type,
                    sort_order: 999,
                    visibility
                }
                try {
                    const upload = await AssetService.addMedia(props.asset_id, payload)
                    file.status = props.hide_complete
                        ? 'hidden'
                        : 'complete'

                    file.media_id = upload?.id
                } catch (error) {
                    console.error('AssetService.addMedia error', error)
                    file.status = 'error'
                }
            }

            const upload_label = computed(() => {
                // return the props.upload_label_text if one was provided
                if (props.upload_label_text && typeof props.upload_label_text == 'string' && props.upload_label_text.length > 0) return props.upload_label_text
                // begin attempt to work out the most appropriate label
                switch(props.media_type) {
                    case 'image':
                        return 'Upload a photo'
                    case 'service-history':
                        return 'Upload service history'
                    case 'spec-sheet':
                        return 'Upload spec-sheet'
                    case 'air-report':
                        return 'Upload AIR report'
                    default:
                        return 'Upload a file'
                }
            })

            watch(
                () => files.value,
                (new_value, old_value) => {
                    setTimeout(() => {
                        uploadFiles()
                    }, 1000)
                }
            )

            const uploadFiles = async () => {
                const uploads = await Promise.all(files.value.map((file, index) => uploadFile(file, index)))
                ASSET_STORE.getMedia(props.asset_id, [props.media_type])
                SYSTEM_STORE.createInteraction('ASSET_MEDIA_UPLOAD', ROUTE.fullPath, props.asset_id, { route_path: ROUTE.fullPath, file_count: files.value.length })
                outcomeReport()
                //resetFileQueue()
            }

            const outcomeReport = () => {
                const failures = files.value.filter(f => f.status == 'error')

                Array.isArray(failures) && failures.length > 0 ? SYSTEM_STORE.addError('Some files failed to upload')
                    : SYSTEM_STORE.addSuccess('Your files have been uploaded')
            }

            return {
                onInputChange,
                addFiles,
                uploadFiles,
                removeFile,
                files,
                uploadable_files,
                upload_label,
                resetFileQueue
            }
        },
    }
</script>